<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { RouterLink } from 'vue-router';
import { ArrowDown } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import { navSections as defaultNavSections, type NavItem, type NavSection } from './nav';

const props = withDefaults(defineProps<{ navSections?: NavSection[] }>(), {
  navSections: () => defaultNavSections,
});

// Brand + accordion nav + footer, shared by the desktop sidebar (MainLayout's
// <aside>) and the mobile drawer so the two never drift. Accordion open-state
// logic was lifted here verbatim from MainLayout.
const { t } = useI18n();
const route = useRoute();

const STORAGE_KEY = 'castvo.nav.open';

function initialOpenSection(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && defaultNavSections.some((s) => s.key === stored)) return stored;
  } catch {
    /* localStorage blocked — fall through */
  }
  return 'manage';
}

const openSection = ref<string | null>(initialOpenSection());
const visibleNavSections = computed(() => props.navSections);

function toggleSection(key: string | undefined) {
  if (!key) return;
  openSection.value = openSection.value === key ? null : key;
  try {
    localStorage.setItem(STORAGE_KEY, openSection.value ?? '');
  } catch {
    /* ignore */
  }
}

function isCollapsed(section: NavSection): boolean {
  return !!section.key && openSection.value !== section.key;
}

function isSectionEntryActive(section: NavSection): boolean {
  return !!section.entry && route.name === section.entry.name;
}

function sectionTitle(section: NavSection): string {
  return section.entry ? t(section.entry.label) : section.title ? t(section.title) : '';
}

function itemTarget(item: NavItem) {
  return item.to ?? { name: item.name };
}

function queryValueMatches(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(actual)) return actual[0] === String(expected);
  return actual === String(expected);
}

function isNavItemActive(item: NavItem): boolean {
  const target = itemTarget(item);
  if (!target || typeof target !== 'object' || !('name' in target)) return false;
  if (target.name !== route.name) return false;

  const query = 'query' in target && target.query ? target.query : {};
  const entries = Object.entries(query);
  if (entries.length > 0) {
    return entries.every(([key, value]) => queryValueMatches(route.query[key], value));
  }

  if (item.name === 'profit' && route.query.anchor === 'byProduct') return false;
  if (item.name === 'profit-drilldown' && route.query.report === 'meta_objective') return false;
  if (item.name === 'creatives' && route.query.view) return false;
  if (item.name === 'one-click-launch' && route.query.view === 'records') return false;
  return true;
}

// Keep the open section synced to the active route so users always see where
// they are. Recomputed reactively off route.name.
const activeSectionKey = computed<string | null>(() => {
  for (const s of visibleNavSections.value) {
    if (!s.key) continue;
    if (s.entry && isNavItemActive(s.entry)) return s.key;
    if (s.items.some((i) => isNavItemActive(i))) return s.key;
  }
  return null;
});

// When the route lands in a section, open it.
import { watch } from 'vue';
watch(activeSectionKey, (key) => {
  if (key && openSection.value !== key) {
    openSection.value = key;
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* ignore */
    }
  }
});
</script>

<template>
  <RouterLink :to="{ name: 'dashboard' }" class="app-sidebar__brand">
    <img src="/castvo-logo.svg" alt="Castvo" class="app-sidebar__logo" />
  </RouterLink>

  <nav class="app-sidebar__nav">
    <template v-for="(section, idx) in visibleNavSections" :key="idx">
      <div
        v-if="section.title"
        class="nav-section-title"
        :class="{ 'nav-section-title--collapsed': isCollapsed(section) }"
      >
        <RouterLink
          v-if="section.entry"
          :to="itemTarget(section.entry)"
          class="nav-section-title__target"
          active-class="nav-section-title__target--active"
          :class="{ 'nav-section-title__target--active': isSectionEntryActive(section) }"
        >
          <span>{{ sectionTitle(section) }}</span>
        </RouterLink>
        <button
          v-else
          type="button"
          class="nav-section-title__target"
          @click="toggleSection(section.key)"
        >
          <span>{{ sectionTitle(section) }}</span>
        </button>
        <button
          type="button"
          class="nav-section-title__toggle"
          :aria-expanded="!isCollapsed(section)"
          :title="sectionTitle(section)"
          @click="toggleSection(section.key)"
        >
          <el-icon :size="12" class="nav-section-title__chevron">
            <ArrowDown />
          </el-icon>
        </button>
      </div>
      <div v-show="!isCollapsed(section)" class="nav-section-items">
        <RouterLink
          v-for="item in section.items"
          :key="item.name"
          :to="itemTarget(item)"
          class="nav-item"
          :class="{ 'nav-item--active': isNavItemActive(item) }"
        >
          <el-icon :size="16" class="nav-item__icon">
            <component :is="item.icon" />
          </el-icon>
          <span>{{ $t(item.label) }}</span>
        </RouterLink>
      </div>
    </template>
  </nav>

  <div class="app-sidebar__foot">
    <span class="app-sidebar__version">v0.1.0</span>
  </div>
</template>

<style scoped>
.app-sidebar__brand {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  border-bottom: 1px solid #e2e8f0;
  text-decoration: none;
}
.app-sidebar__logo {
  height: 28px;
  width: auto;
  display: block;
}
.app-sidebar__nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  color: #0f172a;
  padding: 16px 12px 8px;
  font-family: inherit;
  transition: color 0.15s;
}
.nav-section-title__target {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-decoration: none;
}
.nav-section-title__target:hover,
.nav-section-title__target--active {
  color: #2563eb;
}
.nav-section-title__toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
}
.nav-section-title__chevron {
  transition: transform 0.18s ease;
  opacity: 0.6;
}
.nav-section-title--collapsed .nav-section-title__chevron {
  transform: rotate(-90deg);
}
.nav-section-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  text-decoration: none;
  font-weight: 500;
  transition: background 0.12s, color 0.12s;
  cursor: pointer;
}
.nav-item:hover {
  background: #f1f5f9;
  color: #0f172a;
}
.nav-item__icon {
  color: #94a3b8;
  transition: color 0.12s;
}
.nav-item:hover .nav-item__icon {
  color: #475569;
}
.nav-item--active {
  background: #eff6ff;
  color: #2563eb;
}
.nav-item--active .nav-item__icon {
  color: #2563eb;
}
.app-sidebar__foot {
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
}
.app-sidebar__version {
  font-size: 11px;
  color: #94a3b8;
}
</style>
